// HistoryTab.jsx - 历史记录Tab (最小接入: 复用现有后端趋势接口)
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Button, Space, Select, Tag, message } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { api } from '../../../../services/api';

const COLORS = { primary: '#00d4ff', muted: 'rgba(255,255,255,0.6)', solar: '#f4c542', storage: '#52e8b0', grid: '#3a8dde' };
const cardStyle = { background: 'transparent', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' };

const fmtTime = (v) => (v ? new Date(v).toLocaleString() : '-');
const fmtNum = (v) => (v == null || v === '' || isNaN(v)) ? '-' : Number(v);

const HOUR_OPTIONS = [
  { value: 6, label: '6 小时' },
  { value: 24, label: '24 小时' },
  { value: 48, label: '48 小时' },
  { value: 72, label: '3 天' },
  { value: 168, label: '7 天' },
];

// 控制器历史趋势列
const controllerColumns = [
  { title: '记录时间', dataIndex: 'recorded_at', key: 'recorded_at', width: 170, render: fmtTime },
  { title: 'SOC(%)', dataIndex: 'soc', key: 'soc', width: 90, render: v => <Tag color="green">{fmtNum(v) ?? '-'}</Tag> },
  { title: '电网功率(kW)', dataIndex: 'grid_power_kw', key: 'grid_power_kw', width: 130, render: v => <span style={{ color: COLORS.grid }}>{fmtNum(v) ?? '-'}</span> },
  { title: '负载功率(kW)', dataIndex: 'load_power_kw', key: 'load_power_kw', width: 130, render: v => <span style={{ color: COLORS.solar }}>{fmtNum(v) ?? '-'}</span> },
  { title: '光伏功率(kW)', dataIndex: 'pv_power_kw', key: 'pv_power_kw', width: 130, render: v => fmtNum(v) ?? '-' },
  { title: 'PCS功率(kW)', dataIndex: 'pcs_power_kw', key: 'pcs_power_kw', width: 130, render: v => fmtNum(v) ?? '-' },
];

const HistoryTab = ({ controllers }) => {
  const [deviceId, setDeviceId] = useState('');
  const [hours, setHours] = useState(24);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // 默认选中第一台控制器
  useEffect(() => {
    if (!deviceId && controllers && controllers.length > 0) {
      setDeviceId(controllers[0].id);
    }
  }, [controllers, deviceId]);

  const fetchHistory = useCallback(async () => {
    if (!deviceId) { message.warning('请先选择设备'); return; }
    setLoading(true);
    try {
      // 复用现有后端趋势接口 /telemetry/controller/trend/:device_info_id?hours=
      const json = await api.getTelemetryTrend(deviceId, hours);
      if (json && json.success) {
        const list = (json.data || []).map(r => ({
          recorded_at: r.recorded_at,
          soc: r.soc != null ? Number(r.soc) : null,
          grid_power_kw: r.grid_power_kw != null ? Number(r.grid_power_kw) : null,
          load_power_kw: r.load_power_kw != null ? Number(r.load_power_kw) : null,
          pv_power_kw: r.pv_power_kw != null ? Number(r.pv_power_kw) : null,
          pcs_power_kw: r.pcs_power_kw != null ? Number(r.pcs_power_kw) : null,
        }));
        setRows(list);
      }
    } catch (err) {
      message.error('查询失败: ' + err.message);
      console.error('[HistoryTab] fetch error:', err);
    }
    setLoading(false);
  }, [deviceId, hours]);

  useEffect(() => {
    if (deviceId) fetchHistory();
  }, [deviceId, hours, fetchHistory]);

  const handleExport = () => {
    if (!rows.length) { message.warning('暂无数据可导出'); return; }
    const headers = ['recorded_at', 'soc', 'grid_power_kw', 'load_power_kw', 'pv_power_kw', 'pcs_power_kw'];
    const lines = [headers.join(',')].concat(
      rows.map(r => headers.map(k => (r[k] ?? '')).join(','))
    );
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `controller_history_${deviceId}_${hours}h.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  return (
    <div style={{ padding: '16px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <div style={cardStyle}>
            <div style={{ padding: '12px', color: COLORS.muted, fontSize: 12 }}>控制器设备</div>
            <Select
              value={deviceId || undefined}
              onChange={setDeviceId}
              placeholder="选择控制器"
              style={{ width: 'calc(100% - 24px)', margin: '0 12px 12px' }}
              options={(controllers || []).map(c => ({
                value: c.id,
                label: `${c.station_name || c.sn || c.id} (${c.sn || ''})`,
              }))}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={cardStyle}>
            <div style={{ padding: '12px', color: COLORS.muted, fontSize: 12 }}>时间范围</div>
            <Select
              value={hours}
              onChange={setHours}
              style={{ width: 140, margin: '0 12px 12px' }}
              options={HOUR_OPTIONS}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={cardStyle}>
            <div style={{ padding: '12px', color: COLORS.muted, fontSize: 12 }}>操作</div>
            <Space style={{ margin: '0 12px 12px' }}>
              <Button icon={<ReloadOutlined />} onClick={fetchHistory} type="primary" size="small">查询</Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport} size="small">导出CSV</Button>
            </Space>
          </div>
        </Col>
      </Row>

      <Table
        columns={controllerColumns}
        dataSource={rows}
        rowKey="recorded_at"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        style={{ background: 'transparent' }}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default HistoryTab;